import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Page, PageHeader } from "../components/PageHeader";
import DataTable from "../components/DataTable";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Save } from "lucide-react";

const BURG = { background: "#8B1E1E" };
const CARD = { background: "#FAF7F0", borderColor: "#ECE4D8" };

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [company, setCompany] = useState(null);
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [business, setBusiness] = useState({});
  const [digest, setDigest] = useState({ enabled: true, include_admins: true, recipient_user_ids: [] });
  const [users, setUsers] = useState([]);
  const [profile, setProfile] = useState({ name: user?.name || "", phone: user?.phone || "", password: "" });

  const [seqEdit, setSeqEdit] = useState(null);
  const [taxOpen, setTaxOpen] = useState(false);
  const [taxForm, setTaxForm] = useState({ name: "", rate: 0 });
  const [whOpen, setWhOpen] = useState(false);
  const [whForm, setWhForm] = useState({});

  const load = () => {
    api.get("/company").then((c) => setCompany(c && c.id ? c : null)).catch(() => {});
    api.get("/locations").then(setLocations).catch(() => {});
    api.get("/warehouses").then(setWarehouses).catch(() => {});
    api.get("/settings/taxes").then(setTaxes).catch(() => {});
    api.get("/settings/sequences").then(setSequences).catch(() => {});
    api.get("/settings/business").then(setBusiness).catch(() => {});
    api.get("/settings/owner-digest").then((d) => setDigest({ enabled: d.enabled, include_admins: d.include_admins, recipient_user_ids: d.recipient_user_ids || [] })).catch(() => {});
    api.get("/hr/iam/users", { params: { status: "active" } }).then((r) => setUsers(r || [])).catch(() => {});
  };
  useEffect(load, []);

  const saveCompany = async () => { try { await api.put(`/company/${company.id}`, company); toast.success("Company updated"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveBusiness = async () => { try { await api.put("/settings/business", business); toast.success("Business settings saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveDigest = async () => { try { await api.put("/settings/owner-digest", digest); toast.success("Digest settings saved"); } catch (e) { toast.error(formatApiError(e)); } };
  const saveProfile = async () => { try { await api.put("/auth/profile", profile); toast.success("Profile updated"); refreshUser(); } catch (e) { toast.error(formatApiError(e)); } };
  const saveSeq = async () => { try { await api.put(`/settings/sequences/${seqEdit.type}`, seqEdit); toast.success("Sequence updated"); setSeqEdit(null); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const saveTax = async () => { try { await api.post("/settings/taxes", taxForm); toast.success("Tax added"); setTaxOpen(false); setTaxForm({ name: "", rate: 0 }); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const saveWh = async () => { try { if (whForm.id) await api.put(`/warehouses/${whForm.id}`, whForm); else await api.post("/warehouses", whForm); toast.success("Saved"); setWhOpen(false); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const Field = ({ label, value, onChange, type }) => (
    <div className="space-y-1.5"><Label>{label}</Label><Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} /></div>
  );

  return (
    <Page>
      <PageHeader title="Settings" subtitle="Company, warehouses, taxes, numbering & your profile" />
      <Tabs defaultValue="company">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="company" data-testid="tab-company">Company</TabsTrigger>
          <TabsTrigger value="warehouses" data-testid="tab-warehouses">Warehouses</TabsTrigger>
          <TabsTrigger value="taxes" data-testid="tab-taxes">Tax Rates</TabsTrigger>
          <TabsTrigger value="numbering" data-testid="tab-numbering">Numbering</TabsTrigger>
          <TabsTrigger value="business" data-testid="tab-business">Business</TabsTrigger>
          <TabsTrigger value="digest" data-testid="tab-digest">Owner Digest</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
        </TabsList>

        <TabsContent value="company">
          {company ? (
            <div className="rounded-xl border shadow-sm p-6 max-w-3xl" style={CARD}>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company Name" value={company.name} onChange={(v) => setCompany({ ...company, name: v })} />
                <Field label="Legal Name" value={company.legal_name} onChange={(v) => setCompany({ ...company, legal_name: v })} />
                <Field label="Registration No." value={company.reg_number} onChange={(v) => setCompany({ ...company, reg_number: v })} />
                <Field label="Tax ID (TIN)" value={company.tax_id} onChange={(v) => setCompany({ ...company, tax_id: v })} />
                <Field label="Phone" value={company.phone} onChange={(v) => setCompany({ ...company, phone: v })} />
                <Field label="Email" value={company.email} onChange={(v) => setCompany({ ...company, email: v })} />
                <Field label="Currency" value={company.currency} onChange={(v) => setCompany({ ...company, currency: v })} />
                <Field label="Country" value={company.country} onChange={(v) => setCompany({ ...company, country: v })} />
                <div className="col-span-2"><Field label="Address" value={company.address} onChange={(v) => setCompany({ ...company, address: v })} /></div>
              </div>
              <Button onClick={saveCompany} data-testid="company-save" className="mt-4 text-white" style={BURG}><Save size={16} className="mr-1" /> Save Company</Button>
            </div>
          ) : <div className="rounded-xl border p-8 text-center" style={{ ...CARD, color: "#a58f7c" }}>No company profile configured.</div>}
        </TabsContent>

        <TabsContent value="warehouses">
          <DataTable testIdPrefix="warehouses" searchKeys={["name", "code", "location_name"]}
            columns={[
              { key: "name", label: "Warehouse", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span> },
              { key: "code", label: "Code" },
              { key: "location_name", label: "Location" }, { key: "manager", label: "Manager" },
              { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
              { key: "_a", label: "", sortable: false, align: "right", render: (r) => <button onClick={() => { setWhForm(r); setWhOpen(true); }} className="p-1.5 rounded-lg hover:bg-[#F3ECE2]" style={{ color: "#8B1E1E" }} data-testid={`wh-edit-${r.id}`}><Pencil size={15} /></button> },
            ]} rows={warehouses}
            toolbar={<Button onClick={() => { setWhForm({ status: "active", location_id: locations[0]?.id }); setWhOpen(true); }} data-testid="wh-add" style={BURG} className="text-white"><Plus size={16} className="mr-1" /> New Warehouse</Button>} />
        </TabsContent>

        <TabsContent value="taxes">
          <DataTable testIdPrefix="taxes"
            columns={[
              { key: "name", label: "Tax Name", render: (r) => <span className="font-medium" style={{ color: "#3B2722" }}>{r.name}</span> },
              { key: "rate", label: "Rate (%)", align: "right", render: (r) => `${r.rate}%` },
              { key: "is_default", label: "Default", render: (r) => r.is_default ? <StatusBadge status="active" /> : "—" },
            ]} rows={taxes}
            toolbar={<Button onClick={() => setTaxOpen(true)} data-testid="tax-add" style={BURG} className="text-white"><Plus size={16} className="mr-1" /> New Tax Rate</Button>} />
        </TabsContent>

        <TabsContent value="numbering">
          <DataTable testIdPrefix="sequences"
            columns={[
              { key: "type", label: "Document", render: (r) => <span className="capitalize font-medium" style={{ color: "#3B2722" }}>{r.type.replace(/_/g, " ")}</span> },
              { key: "prefix", label: "Prefix", render: (r) => <span className="font-mono">{r.prefix}</span> },
              { key: "padding", label: "Padding" },
              { key: "next", label: "Next #" },
              { key: "_ex", label: "Example", render: (r) => <span className="font-mono" style={{ color: "#8B1E1E" }}>{r.prefix}{String(r.next).padStart(r.padding, "0")}</span> },
              { key: "_a", label: "", sortable: false, align: "right", render: (r) => <button onClick={() => setSeqEdit(r)} className="p-1.5 rounded-lg hover:bg-[#F3ECE2]" style={{ color: "#8B1E1E" }} data-testid={`seq-edit-${r.type}`}><Pencil size={15} /></button> },
            ]} rows={sequences} />
        </TabsContent>

        <TabsContent value="business">
          <div className="rounded-xl border shadow-sm p-6 max-w-2xl" style={CARD}>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Currency Symbol" value={business.currency_symbol} onChange={(v) => setBusiness({ ...business, currency_symbol: v })} />
              <Field label="Default Tax Rate (%)" type="number" value={business.default_tax_rate} onChange={(v) => setBusiness({ ...business, default_tax_rate: Number(v) })} />
              <div className="col-span-2"><Field label="Receipt Footer" value={business.receipt_footer} onChange={(v) => setBusiness({ ...business, receipt_footer: v })} /></div>
            </div>
            <Button onClick={saveBusiness} data-testid="business-save" className="mt-4 text-white" style={BURG}><Save size={16} className="mr-1" /> Save Settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="digest">
          <div className="rounded-xl border shadow-sm p-6 max-w-3xl space-y-4" style={CARD} data-testid="digest-settings">
            <p className="text-sm" style={{ color: "#765A4A" }}>
              The digest lands in-app every morning at 7:00 (Lagos) and covers the previous business day —
              sales, gross margin, till cash variance, low stock and approvals still waiting.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Digest enabled</Label>
                <Select value={String(digest.enabled ?? true)} onValueChange={(v) => setDigest({ ...digest, enabled: v === "true" })}>
                  <SelectTrigger data-testid="digest-enabled"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Always include admins</Label>
                <Select value={String(digest.include_admins ?? true)} onValueChange={(v) => setDigest({ ...digest, include_admins: v === "true" })}>
                  <SelectTrigger data-testid="digest-include-admins"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="true">Yes</SelectItem><SelectItem value="false">No</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Recipients</Label>
              <p className="text-xs mb-2" style={{ color: "#a58f7c" }}>Pick who receives the digest notification.</p>
              <div className="rounded-lg border max-h-64 overflow-y-auto divide-y" style={{ borderColor: "#ECE4D8", background: "#fff" }}>
                {users.map((u) => {
                  const on = (digest.recipient_user_ids || []).includes(u.id);
                  return (
                    <label key={u.id} data-testid={`digest-recipient-${u.id}`} className="flex items-center gap-3 px-3 py-2 cursor-pointer">
                      <input type="checkbox" checked={on} onChange={() => setDigest({
                        ...digest,
                        recipient_user_ids: on ? (digest.recipient_user_ids || []).filter((x) => x !== u.id)
                          : [...(digest.recipient_user_ids || []), u.id],
                      })} />
                      <span className="text-sm" style={{ color: "#3B2722" }}>{u.name}
                        <span className="ml-2 text-xs" style={{ color: "#a58f7c" }}>{u.email} · {u.role_name || "—"}</span>
                      </span>
                    </label>
                  );
                })}
                {users.length === 0 && <div className="px-3 py-4 text-sm" style={{ color: "#a58f7c" }}>No users found.</div>}
              </div>
            </div>
            <Button onClick={saveDigest} data-testid="digest-save" className="text-white" style={BURG}><Save size={16} className="mr-1" /> Save Digest Settings</Button>
          </div>
        </TabsContent>

        <TabsContent value="profile">
          <div className="rounded-xl border shadow-sm p-6 max-w-lg" style={CARD}>
            <div className="space-y-4">
              <Field label="Full Name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} />
              <Field label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} />
              <div className="space-y-1.5"><Label>New Password (blank to keep)</Label><Input type="password" value={profile.password} onChange={(e) => setProfile({ ...profile, password: e.target.value })} data-testid="profile-password" /></div>
            </div>
            <Button onClick={saveProfile} data-testid="profile-save" className="mt-4 text-white" style={BURG}><Save size={16} className="mr-1" /> Update Profile</Button>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!seqEdit} onOpenChange={() => setSeqEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-brand">Edit Numbering</DialogTitle></DialogHeader>
          {seqEdit && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Prefix</Label><Input value={seqEdit.prefix} onChange={(e) => setSeqEdit({ ...seqEdit, prefix: e.target.value })} data-testid="seq-prefix" /></div>
              <div className="space-y-1.5"><Label>Padding (digits)</Label><Input type="number" value={seqEdit.padding} onChange={(e) => setSeqEdit({ ...seqEdit, padding: Number(e.target.value) })} /></div>
              <div className="space-y-1.5"><Label>Next Number</Label><Input type="number" value={seqEdit.next} onChange={(e) => setSeqEdit({ ...seqEdit, next: Number(e.target.value) })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={saveSeq} data-testid="seq-save" className="w-full text-white" style={BURG}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={taxOpen} onOpenChange={setTaxOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-brand">New Tax Rate</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={taxForm.name} onChange={(e) => setTaxForm({ ...taxForm, name: e.target.value })} data-testid="tax-name" /></div>
            <div className="space-y-1.5"><Label>Rate (%)</Label><Input type="number" value={taxForm.rate} onChange={(e) => setTaxForm({ ...taxForm, rate: e.target.value })} data-testid="tax-rate" /></div>
          </div>
          <DialogFooter><Button onClick={saveTax} data-testid="tax-save" className="w-full text-white" style={BURG}>Add Tax</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={whOpen} onOpenChange={setWhOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-brand">{whForm.id ? "Edit Warehouse" : "New Warehouse"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Name</Label><Input value={whForm.name || ""} onChange={(e) => setWhForm({ ...whForm, name: e.target.value })} data-testid="wh-name" /></div>
            <div className="space-y-1.5"><Label>Code</Label><Input value={whForm.code || ""} onChange={(e) => setWhForm({ ...whForm, code: e.target.value })} /></div>
            <div className="col-span-2 space-y-1.5"><Label>Location</Label>
              <Select value={whForm.location_id || ""} onValueChange={(v) => setWhForm({ ...whForm, location_id: v })}>
                <SelectTrigger data-testid="wh-location"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Manager</Label><Input value={whForm.manager || ""} onChange={(e) => setWhForm({ ...whForm, manager: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Status</Label>
              <Select value={whForm.status || "active"} onValueChange={(v) => setWhForm({ ...whForm, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={saveWh} data-testid="wh-save" className="w-full text-white" style={BURG}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
